import React, { useState, useRef } from 'react';
import { Upload, X, File, FileText, Image as ImageIcon, Loader } from 'lucide-react';
import { prepareFileForUpload, createFilePreview, formatFileSize } from '../../utils/fileUpload';
import Button from './Button';

const FileUpload = ({ 
  onUpload, 
  currentFile = null,
  onDelete = null,
  accept = 'image/*,application/pdf',
  maxSize = 10 * 1024 * 1024, // 10MB
  label = 'Dosya Yükle',
  description = 'WEBP, JPG, PNG veya PDF (Max 10MB)',
  convertToWebP = true,
  className = ''
}) => {
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setError(null);
    setUploading(true);

    try {
      // Dosyayı hazırla (validasyon, dönüştürme, boyutlandırma)
      const processedFile = await prepareFileForUpload(file, {
        maxSize,
        convertToWebP: convertToWebP && file.type.startsWith('image/'),
        resize: file.type.startsWith('image/'),
      });

      // Önizleme oluştur
      const previewUrl = await createFilePreview(processedFile);
      setPreview(previewUrl);

      // Upload callback
      if (onUpload) {
        await onUpload(processedFile);
      }
    } catch (err) {
      setError(err.message);
      console.error('File upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = () => {
    setPreview(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onDelete) {
      onDelete();
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const renderPreview = () => {
    const fileUrl = preview || currentFile;
    
    if (!fileUrl) return null;

    // PDF preview
    if (fileUrl === 'pdf' || (typeof fileUrl === 'string' && fileUrl.includes('.pdf'))) {
      return (
        <div className="relative group">
          <div className="flex items-center justify-center h-48 bg-red-50 rounded-lg border-2 border-red-200">
            <FileText size={64} className="text-red-500" />
          </div>
          <button
            onClick={handleDelete}
            className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
          >
            <X size={16} />
          </button>
        </div>
      );
    }

    // Document preview
    if (fileUrl === 'document') {
      return (
        <div className="relative group">
          <div className="flex items-center justify-center h-48 bg-blue-50 rounded-lg border-2 border-blue-200">
            <File size={64} className="text-blue-500" />
          </div>
          <button
            onClick={handleDelete}
            className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
          >
            <X size={16} />
          </button>
        </div>
      );
    }

    // Image preview
    return (
      <div className="relative group">
        <img
          src={fileUrl}
          alt="Preview"
          className="w-full h-48 object-cover rounded-lg border-2 border-gray-200"
        />
        <button
          onClick={handleDelete}
          className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
        >
          <X size={16} />
        </button>
      </div>
    );
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
        disabled={uploading}
      />

      {(preview || currentFile) ? (
        renderPreview()
      ) : (
        <div
          onClick={handleClick}
          className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-all"
        >
          {uploading ? (
            <div className="flex flex-col items-center">
              <Loader size={48} className="text-primary-600 animate-spin mb-2" />
              <p className="text-sm text-gray-600">Yükleniyor...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <Upload size={48} className="text-gray-400 mb-2" />
              <p className="text-sm font-medium text-gray-700 mb-1">
                Dosya seçmek için tıklayın
              </p>
              {description && (
                <p className="text-xs text-gray-500">{description}</p>
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
